<template>
  <section id="app">
    <div class="grid-x">
      <!-- SIDE-NAV -->
      <div class="cell shrink nav-container" v-if="!hideSideBar">
        <side-nav
          :organizations="organizations"
          :activeOrganizationId="activeOrganizationId"
          :isOwner="isActiveOrganizationOwner"
          @switchOrg="onSwitchOrg"
        />
      </div>

      <!-- CONTENT -->
      <div class="cell auto page-container">
        <!-- TODO : vérifier si on est sur un plan payant -->
        <div v-if="showCardError && !hideSideBar" class="card-alert error-card-alert">
          <p>
            <strong>Impossible to find your credit card informations.</strong> Please
            <router-link :to="{ name: 'card' }">check your credit card</router-link> informations.
          </p>
        </div>
        <top-nav v-if="!hideSideBar" :me="me" :isOwner="isActiveOrganizationOwner" />
        <router-view
          v-if="canRenderRoute"
          :key="activeOrganizationId || ''"
          :me="me"
          :isOwner="isActiveOrganizationOwner"
          @updateCardInformation="updateCardInformation"
          @organizationContextChanged="handleOrganizationContextChanged"
          @organizationUpdated="handleOrganizationUpdated"
          :isCardActive="isCardActive"
        />
      </div>
    </div>

    <!-- GLOBAL BASICS COMPONENTS INTEGRATION -->
    <app-message />
    <app-notifications />
  </section>
</template>

<script>
import stripeApi from '@/services/api/stripe';
import userApi from '@/services/api/user';
import organizationApi from '@/services/api/organization';
import SideNav from '@/components/SideNav.vue';
import TopNav from '@/components/TopNav.vue';
import auth from '@/services/auth';
import activeOrganization from '@/services/activeOrganization';

export default {
  name: 'app',

  components: {
    SideNav,
    TopNav,
  },

  data() {
    return {
      isLoggedIn: auth.isLoggedIn(),
      isCardActive: true,
      me: null,
      showCardError: false,
      organizations: [],
      activeOrganizationId: activeOrganization.get(),
      isOrganizationContextReady: false,
      isOrganizationContextLoading: false,
      organizationContextRefreshId: 0,
    };
  },

  mounted() {
    this.init();
  },

  watch: {
    async $route() {
      const wasLoggedIn = this.isLoggedIn;
      this.isLoggedIn = auth.isLoggedIn();

      if (!this.isLoggedIn) {
        this.organizationContextRefreshId += 1;
        activeOrganization.clear();
        this.me = null;
        this.organizations = [];
        this.activeOrganizationId = null;
        this.isOrganizationContextReady = false;
        this.isOrganizationContextLoading = false;
        return;
      }

      // Navigating keeps the organization context: it is only reloaded at login, on a switch
      // and after an organization update, so that pages are not remounted on every route change.
      const needsContext = this.requiresOrganizationContext
        && !this.isOrganizationContextReady
        && !this.isOrganizationContextLoading;
      if (!wasLoggedIn || needsContext) {
        await this.refreshOrganizationContext();
        return;
      }

      // While the context loads, the role is unknown: the refresh enforces owner routes itself.
      if (this.isOrganizationContextReady) {
        this.enforceOwnerRoute();
      }
    },
  },

  computed: {
    hideSideBar() {
      return this.$route.meta.hideSideBar;
    },
    activeOrganization() {
      return this.organizations.find(
        (org) => org.organization.organizationId === this.activeOrganizationId,
      );
    },
    activeOrganizationRole() {
      return this.activeOrganization ? this.activeOrganization.role : null;
    },
    currentOrganizationRole() {
      return this.activeOrganizationRole || (this.me ? this.me.organizationRole : null);
    },
    isActiveOrganizationOwner() {
      return this.currentOrganizationRole === 'OWNER';
    },
    currentRouteRequiresOwner() {
      return this.$route.meta && this.$route.meta.requiresOwner;
    },
    requiresOrganizationContext() {
      return this.isLoggedIn && !this.hideSideBar;
    },
    canRenderRoute() {
      if (this.requiresOrganizationContext && !this.isOrganizationContextReady) {
        return false;
      }
      return !this.currentRouteRequiresOwner || this.isActiveOrganizationOwner;
    },
  },

  methods: {
    async init() {
      this.isLoggedIn = auth.isLoggedIn();

      if (this.requiresOrganizationContext) {
        await this.refreshOrganizationContext();
      }
    },

    /**
     * A background refresh keeps the current page mounted: it is used after the page itself
     * changed the organization (name, plan, API key).
     */
    async refreshOrganizationContext({ background = false } = {}) {
      const refreshId = this.organizationContextRefreshId + 1;
      this.organizationContextRefreshId = refreshId;
      this.isOrganizationContextLoading = true;
      if (!background) {
        this.isOrganizationContextReady = false;
        this.showCardError = false;
        this.isCardActive = true;
      }

      try {
        if (!(await this.getOrganizations(refreshId))) return;
        if (!(await this.getMe(refreshId))) return;
        this.selectDefaultOrganizationIfNeeded();

        if (this.isActiveOrganizationOwner) {
          if (!(await this.getCards(refreshId))) return;
        } else {
          this.showCardError = false;
          this.isCardActive = true;
        }

        if (!this.isCurrentOrganizationRefresh(refreshId)) return;
        this.enforceOwnerRoute();
        this.isOrganizationContextReady = true;
      } catch (error) {
        if (!this.isCurrentOrganizationRefresh(refreshId)) return;
        this.me = null;
        this.showCardError = false;
        this.isCardActive = true;
      } finally {
        if (this.isCurrentOrganizationRefresh(refreshId)) {
          this.isOrganizationContextLoading = false;
        }
      }
    },

    // Without a valid stored organization, /dashboard/me was called without header and
    // answered for the personal organization: it becomes this tab's active organization.
    selectDefaultOrganizationIfNeeded() {
      if (this.activeOrganizationId || !this.organizations.length) return;

      const personalOrganization = this.me
        ? this.organizations.find(
          (o) => o.organization.organizationId === this.me.organizationId,
        )
        : null;
      const { organizationId } = (personalOrganization || this.organizations[0]).organization;
      activeOrganization.set(organizationId);
      this.activeOrganizationId = organizationId;
    },

    isCurrentOrganizationRefresh(refreshId) {
      return (
        refreshId === undefined
        || refreshId === this.organizationContextRefreshId
      );
    },

    enforceOwnerRoute() {
      if (this.currentRouteRequiresOwner && !this.isActiveOrganizationOwner) {
        this.$router.replace({ name: 'home' }).catch(() => null);
      }
    },

    /**
     * On vérifie les informations de la carte à 3 moments :
     * • Au démarrage de l'app
     * • Au login
     * • Lorsqu'on se trouve sur la page des cartes
     *
     * L'objet peut contenir :
     * • une propriété cards avec la liste des cartes
     * • une propriété error si la récupération des cartes a échoué
     *
     * @param {object} data liste des cartes trouvées
     */
    updateCardInformation(data) {
      // Reset l'état actuel
      this.showCardError = false;

      if (data && data.error) {
        this.showCardError = true;
      } else if (data && data.cards) {
        this.isCardActive = data.cards && !!data.cards.data.length;
      } else {
        this.getCards();
      }
    },

    async getMe(refreshId) {
      try {
        const me = await userApi.getMe();
        if (!this.isCurrentOrganizationRefresh(refreshId)) return false;
        this.me = me;
        return true;
      } catch (er) {
        if (!this.isCurrentOrganizationRefresh(refreshId)) return false;
        this.me = null;
        this.$message.show({
          title: 'Erreur',
          text: 'Impossible to find user',
          confirmText: 'Ok',
          hasCancel: false,
        });
        throw er;
      }
    },

    async getOrganizations(refreshId) {
      try {
        const organizations = await organizationApi.getOrganizations();
        if (!this.isCurrentOrganizationRefresh(refreshId)) return false;
        this.organizations = organizations;

        const storedOrgId = activeOrganization.get();
        const isValid = this.organizations.some(
          (o) => o.organization.organizationId === storedOrgId,
        );

        if (isValid) {
          activeOrganization.set(storedOrgId);
          this.activeOrganizationId = storedOrgId;
        } else {
          activeOrganization.clear();
          this.activeOrganizationId = null;
        }
        return true;
      } catch (er) {
        if (!this.isCurrentOrganizationRefresh(refreshId)) return false;
        this.organizations = [];
        this.$message.show({
          title: 'Error',
          text: 'Unable to load organizations. Please refresh the page or contact support.',
          confirmText: 'Ok',
          hasCancel: false,
        });
        throw er;
      }
    },

    async onSwitchOrg(organizationId) {
      activeOrganization.set(organizationId);
      this.activeOrganizationId = organizationId;
      await this.refreshOrganizationContext();
    },

    async handleOrganizationContextChanged() {
      activeOrganization.clear();
      this.activeOrganizationId = null;
      await this.refreshOrganizationContext();
    },

    async handleOrganizationUpdated() {
      await this.refreshOrganizationContext({ background: true });
    },

    // Récupération des informations de la carte
    async getCards(refreshId) {
      try {
        const cards = await stripeApi.getCards();
        if (!this.isCurrentOrganizationRefresh(refreshId)) return false;
        this.isCardActive = cards && !!cards.data.length;
        return true;
      } catch (er) {
        if (!this.isCurrentOrganizationRefresh(refreshId)) return false;
        if (er.response && er.response.status === 403) {
          this.showCardError = false;
          this.isCardActive = true;
          return true;
        }
        this.showCardError = true;
        return true;
      }
    },
  },
};
</script>
<style lang="sass">
@import "@/assets/sass/app.sass"

#app
  .page-container
    position: relative
    padding: 0
    background-color: $primary95

  .nav-container
    position: relative
    background-color: $primary100

  .card-alert
    width: 100%
    padding: 12px

    p, a
      text-align: center
      color: white

    a
      text-decoration: underline
      @include title-xsmall

  .error-card-alert
    background-color: $warning

  .no-card-alert
    background-color: $primary
</style>
